import React from 'react';
import PropTypes from 'prop-types';
import categoryHelper from 'helpers/category';
import compose from 'recompose/compose';
import languagesHelper from 'helpers/languages';
import PhoneInput from 'components/form/InputFields/MobianPhoneInput/PhoneInput';
import RenderAutoCompleteInputField from 'components/form/InputFields/RenderAutoCompleteInputField';
import RenderTextInputField from 'components/form/InputFields/RenderTextInputField';
import renderTextareaInputField from 'components/form/InputFields/RenderTextareaInputField';
import SubmitButton from 'components/button/SubmitButton';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import validations from 'helpers/forms/validations';
import { ButtonWrapper, FormWrapper, Item, Row, Section } from 'components/ui/table/elements';
import { Field, isInvalid, reduxForm } from 'redux-form';
import { connect } from 'react-redux';

class BookingsEditForm extends React.Component {
    constructor() {
        super();

        this.systemLanguage = navigator.language.split('-')[0];
    }

    componentDidMount() {
        const {
            data,
        } = this.props;

        if (!utils.isEmpty(data)) {
            this.handleInitialize(data);
        }
    }

    componentDidUpdate(prevProps) {
        const {
            data,
        } = this.props;

        if (utils.get(prevProps.data, 'id') !== utils.get(data, 'id')) {
            this.handleInitialize(data);
        }
    }

    handleInitialize = (data) => {
        const {
            initialize,
        } = this.props;

        return initialize({
            customer_remarks: data.customer_remarks,
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
            license_plate: data.license_plate,
            locale: data.locale,
            phone: data.phone,
            return_flight_number: data.return_flight_number,
        });
    }

    render() {
        const {
            data,
            handleSubmit,
            handleSubmitForm,
            submitting,
            translate,
        } = this.props;

        const categoryId = data.product_info.category_id;
        const categoryIsParking = categoryHelper.isParking(categoryId);
        const categoryIsHubBooking = categoryHelper.isHub(categoryId);

        const {
            config: {
                language: {
                    supported,
                },
            },
        } = this.context;

        return (
            <FormWrapper>
                <form onSubmit={ handleSubmit(handleSubmitForm) }>
                    <Section title={ translate('resources.bookings.forms.general') }>
                        <Row>
                            <Item>
                                <Field
                                    component={ RenderTextInputField }
                                    fullWidth={ true }
                                    label={ translate('resources.bookings.fields.firstName') }
                                    name='first_name'
                                    validate={ validations.required }
                                />
                            </Item>

                            <Item>
                                <Field
                                    component={ RenderTextInputField }
                                    fullWidth={ true }
                                    label={ translate('resources.bookings.fields.lastName') }
                                    name='last_name'
                                    validate={ validations.required }
                                />
                            </Item>
                        </Row>

                        <Row>
                            <Item>
                                <Field
                                    component={ RenderTextInputField }
                                    fullWidth={ true }
                                    label={ translate('resources.bookings.fields.email') }
                                    name='email'
                                    type='email'
                                    validate={ validations.required }
                                />
                            </Item>
                        </Row>

                        <Row>
                            <Item>
                                <Field
                                    component={ PhoneInput }
                                    fullWidth={ true }
                                    label={ translate('resources.bookings.fields.phone') }
                                    name='phone'
                                    validate={ validations.required }
                                />
                            </Item>
                        </Row>

                        { (categoryIsParking || categoryIsHubBooking) &&
                            <Row>
                                <Item>
                                    <Field
                                        component={ RenderTextInputField }
                                        fullWidth={ true }
                                        label={ translate('resources.bookings.fields.licensePlate') }
                                        name='license_plate'
                                    />
                                </Item>
                            </Row>
                        }

                        <Row>
                            <Item>
                                <Field
                                    component={ RenderTextInputField }
                                    fullWidth={ true }
                                    label={ translate('resources.orders.fields.return_flight_number') }
                                    name='return_flight_number'
                                />
                            </Item>
                        </Row>
                    </Section>

                    <Section title={ translate('resources.bookings.forms.additional') }>
                        <Row>
                            <Item>
                                <Field
                                    choices={ languagesHelper.getLanguageChoices(supported, translate) }
                                    component={ RenderAutoCompleteInputField }
                                    label={ translate('resources.bookings.fields.locale') }
                                    name='locale'
                                    optionText='label'
                                    optionValue='value'
                                    fullWidth={ true }
                                    startingValue= {
                                        supported.includes(this.systemLanguage) ? this.systemLanguage : data.locale
                                    }
                                    validate={ validations.required }
                                />
                            </Item>
                        </Row>

                        <Row>
                            <Item>
                                <Field
                                    component={ renderTextareaInputField }
                                    label={ translate('resources.bookings.fields.customerRemarks') }
                                    maxLength={ 255 }
                                    multiLine={ true }
                                    name='customer_remarks'
                                    rows={ 4 }
                                />
                            </Item>
                        </Row>
                    </Section>

                    <Section>
                        <Row>
                            <ButtonWrapper>
                                <SubmitButton
                                    disabled={ submitting }
                                    loading={ submitting }
                                />
                            </ButtonWrapper>
                        </Row>
                    </Section>
                </form>
            </FormWrapper>
        );
    }
}

BookingsEditForm.contextTypes = {
    config: PropTypes.object,
};

BookingsEditForm.propTypes = {
    data: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    handleSubmitForm: PropTypes.func.isRequired,
    initialize: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

const mapStateToProps = (state, props) => {
    const {
        form,
    } = props;

    return {
        invalid: isInvalid(form)(state),
    };
};

const enhance = compose(
    translate,
    connect(
        mapStateToProps,
        null,
    ),
    reduxForm({
        validate: (values, props) => {
            const errors = {};
            const { translate } = props;

            if (!values.first_name) {
                errors.first_name = translate('forms.required');
            }

            if (!values.last_name) {
                errors.last_name = translate('forms.required');
            }

            if (!values.email) {
                errors.email = translate('forms.required');
            }

            if (!values.phone) {
                errors.phone = translate('forms.required');
            }

            return errors;
        },
    }),
);

export default enhance(BookingsEditForm);



// WEBPACK FOOTER //
// ./src/pages/bookings/edit/BookingsEditForm.js